import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BASE_URL, API } from "../api";
import Layout from "../components/Layout";
import { FiTrash2, FiEdit, FiEye, FiSearch, FiClipboard } from "react-icons/fi";
import Pagination from "../components/Pagination";

export default function ManageCars() {
  const navigate = useNavigate();

  // STATE

  const [groups, setGroups] = useState([]);
  const [data, setData] = useState([]);
  const [sequenceTotal, setSequenceTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [filterGroup, setFilterGroup] = useState(0);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // View modal
  const [viewCar, setViewCar] = useState(null);

  // Delete modal
  const [deleteCar, setDeleteCar] = useState(null);

  // Toast
  const [toast, setToast] = useState(null);

  const PER = 10;

  // AUTHENTICATION

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  // TOAST

  const toast2 = (msg, type = "success") => {
    setToast({
      msg,
      type,
    });

    setTimeout(() => {
      setToast(null);
    }, 3500);
  };

  // LOAD GROUPS

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await API.get("/cargroup", {
          params: {
            page: 1,
            limit: 100,
          },
        });

        setGroups(response.data?.data || []);
      } catch (error) {
        console.error(error);

        toast2(
          error.response?.data?.error || "Failed to load car groups",
          "error"
        );
      }
    };

    fetchGroups();
  }, []);

  // FETCH CARS

  const fetchData = useCallback(async (pg = 1, q = "", gid = 0) => {
    setLoading(true);

    try {
      const response = await API.get("/cars", {
        params: {
          page: pg,
          limit: PER,
          search: q,
          selectedcartype: gid,
        },
      });

      setData(response.data?.data || []);

      setTotalPages(response.data?.totalPages || 1);

      setTotal(response.data?.total || 0);
      setSequenceTotal(response.data?.seqnume || 0);
    } catch (error) {
      console.error(error);

      toast2(error.response?.data?.error || "Failed to load cars", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  // INITIAL / PAGE / FILTER LOAD

  useEffect(() => {
    fetchData(page, search, filterGroup);
  }, [page, search, filterGroup, fetchData]);

  // SEARCH DEBOUNCE

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);

      fetchData(1, search, filterGroup);
    }, 400);

    return () => clearTimeout(timer);
  }, [search, fetchData, filterGroup]);

  // ADD CAR

  const handleAddCar = () => {
    navigate("/manage-cars/add");
  };

  // EDIT CAR

  const handleEditCar = (row) => {
    navigate("/manage-cars/edit", {
      state: {
        car: row,
      },
    });
  };

  // VIEW CAR

  const handleViewCar = (row) => {
    setViewCar(row);
  };

  // OPEN DELETE MODAL

  const openDelete = (row) => {
    setDeleteCar(row);
  };

  // DELETE CAR

  const handleDelete = async () => {
    if (!deleteCar) return;

    setSaving(true);

    try {
      await API.delete(`/cars/${deleteCar.carmodel_id}`);

      toast2("Car deleted successfully", "success");

      setDeleteCar(null);

      // If last item on current page,
      // move to previous page.
      if (data.length === 1 && page > 1) {
        setPage((previousPage) => previousPage - 1);
      } else {
        fetchData(page, search, filterGroup);
      }
    } catch (error) {
      console.error(error);

      toast2(error.response?.data?.error || "Delete failed", "error");
    } finally {
      setSaving(false);
    }
  };

  // GROUP FILTER

  const handleGroupFilter = (event) => {
    const value = Number(event.target.value);
    setFilterGroup(value);
    setPage(1);
  };

  const handleDisplayHomeToggle = async (row) => {
    const currentStatus = Number(row.carmodel_dispinhome);

    const newStatus = currentStatus === 2 ? 1 : 2;

    try {
      await API.patch(`/cars/${row.carmodel_id}/home-offer`, {
        car_dispinhome: newStatus,
      });

      toast2(
        newStatus === 2
          ? "Car displayed on home successfully"
          : "Car removed from home successfully",
        "success"
      );

      fetchData(page, search, filterGroup);
    } catch (error) {
      console.error(error);

      toast2(
        error.response?.data?.error || "Failed to update home display",
        "error"
      );
    }
  };

  const handleSequenceChange = async (row, newSequence) => {
    try {
      await API.patch(`/cars/${row.carmodel_id}/sequence-change`, {
        sequence: newSequence,
      });

      toast2("Sequence updated successfully", "success");

      fetchData(page, search, filterGroup);
    } catch (error) {
      console.error("Sequence update error:", error);

      toast2(
        error.response?.data?.error || "Failed to update sequence",
        "error"
      );
    }
  };
// =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };
  // RENDER

  return (
    <Layout activePage="Car Details">
      {/*
          MAIN
      */}

      <main className="dashboard-main">
        {/*
            TABLE
        */}
        <div className="table-card">
          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />
              <span>Manage Car Model</span>

              <span className="rec-count ms-1">{total} slabs</span>
            </h3>
            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch />
              </span>

              <input
                type="text"
                placeholder="Search car..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);

                  setPage(1);
                }}
                className="search-input"
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={() => {
                    setSearch("");

                    setPage(1);
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            <select
              className="season-input"
              value={filterGroup}
              onChange={handleGroupFilter}
            >
              <option value={0}>— All Groups —</option>

              {groups.map((group) => (
                <option key={group.cat_id} value={group.cat_id}>
                  {group.cat_name}
                </option>
              ))}
            </select>
            {/* ADD BUTTON */}
            <button
              type="button"
              className="um-btn-primary"
              onClick={handleAddCar}
            >
              <span className="um-add-icon">＋</span>
              Add Car
            </button>
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-hover table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr className="um-table-head">
                  <th className="text-center">#</th>
                  <th className="text-center">Car Name</th>
                  <th className="text-center">Group</th>
                  <th className="text-center">Brand</th>
                  <th className="text-center">Item Name</th>
                  <th className="text-center">Display Home</th>
                  <th className="text-center">Sequence</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {/*  LOADING */}

                {loading ? (
                  Array.from({
                    length: 6,
                  }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({
                        length: 7,
                      }).map((_, j) => (
                        <td key={j} className="text-center">
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  /*   EMPTY */

                  <tr>
                    <td colSpan={8} className="text-center py-5">
                      <div className="empty-icon">
                        <FiSearch size={18} />
                      </div>

                      <div className="empty-title">No cars found</div>
                    </td>
                  </tr>
                ) : (
                  /*  DATA */

                  data.map((row, index) => (
                    <tr key={row.car_id} className="um-row">
                      {/* NUMBER */}

                      <td className="text-center">
                        {(page - 1) * PER + index + 1}
                      </td>

                      {/* CAR NAME */}

                      <td className="text-center">{row.carmodel_name}</td>

                      {/* GROUP */}

                      <td className="text-center">{row.cat_name || "—"}</td>

                      {/* STOCK */}

                      <td className="text-center">{row.brand_name ?? 0}</td>

                      {/* Included itemnames HOME */}
                      <td className="text-center">
                        {row.added_items?.length > 0
                          ? row.added_items
                            .map((item) => item.item_name || "Unnamed item")
                            .join(", ")
                          : "—"}
                      </td>
                      {/* DISPLAY HOME */}

                      <td className="um-td">
                        <div className="status-wrap">
                          <div
                            className={`status-toggle ${Number(row.carmodel_dispinhome) === 2
                                ? "status-active"
                                : "status-inactive"
                              }`}
                            onClick={() => handleDisplayHomeToggle(row)}
                            aria-label={
                              Number(row.carmodel_dispinhome) === 2
                                ? "Remove from home offer"
                                : "Add to home offer"
                            }
                          >
                            <span
                              className={`status-knob ${Number(row.carmodel_dispinhome) === 2
                                  ? "knob-active"
                                  : "knob-inactive"
                                }`}
                            />
                          </div>

                          <span
                            className={`status-text ${Number(row.carmodel_dispinhome) === 2
                                ? "text-active"
                                : "text-inactive"
                              }`}
                          >
                            {Number(row.carmodel_dispinhome) === 2
                              ? "Yes"
                              : "No"}
                          </span>
                        </div>
                      </td>

                      {/* Sequence HOME */}
                      <td className="text-center">
                        <select
                          className="form-select form-select-sm"
                          value={row.seq ?? 0}
                          onChange={(e) =>
                            handleSequenceChange(row, Number(e.target.value))
                          }
                        >
                          <option value={0}>00</option>

                          {Array.from(
                            { length: sequenceTotal },
                            (_, index) => index + 1
                          ).map((seq) => (
                            <option key={seq} value={seq}>
                              {String(seq).padStart(2, "0")}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* ACTIONS */}

                      <td>
                        <div className="action-buttons d-flex justify-content-center gap-2">
                          {/* VIEW */}

                          <button
                            type="button"
                            className="action-btn action-view btn btn-sm"
                            title="View"
                            onClick={() => handleViewCar(row)}
                          >
                            <FiEye />
                          </button>

                          {/* EDIT */}

                          <button
                            type="button"
                            className="action-btn action-edit btn btn-sm"
                            title="Edit"
                            onClick={() => handleEditCar(row)}
                          >
                            <FiEdit />
                          </button>

                          {/* DELETE */}

                          <button
                            type="button"
                            className="action-btn action-delete btn btn-sm"
                            title="Delete"
                            onClick={() => openDelete(row)}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/*
              PAGINATION
          */}

              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={10}
                loading={loading}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
          </div>
        </div>
      </main>

      {/*   VIEW CAR MODAL */}

      {viewCar && (
        <div className="um-overlay" onClick={() => setViewCar(null)}>
          <div
            className="um-modal um-view-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}

            <div className="um-modal-head">
              <h2 className="um-modal-title">Car Details</h2>

              <button className="um-x-btn" onClick={() => setViewCar(null)}>
                ✕
              </button>
            </div>

            {/* BODY */}

            <div className="um-modal-scroll">
              {/* IMAGE */}

              {viewCar.carmodel_image && (
                <div className="profile-header">
                  <img
                    src={`${BASE_URL}/${viewCar.carmodel_image}`}
                    alt={viewCar.image_alt || viewCar.carmodel_image}
                    crossOrigin="anonymous"
                    className="car-profile-image"
                    style={{
                      maxHeight: "300px",
                      width: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}

              {/* TITLE */}

              <div className="profile-header">
                <h3 className="profile-name">{viewCar.carmodel_name}</h3>
              </div>

              {/* DETAILS */}
              <div className="sec-div">
                <span>BASIC INFORMATION</span>
              </div>
              <div className="row g-3">
                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Car Module Name</div>
                      <div className="profile-row-value">
                        {viewCar.carmodel_name || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Group</div>
                      <div className="profile-row-value">
                        {viewCar.cat_name || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Brand</div>
                      <div className="profile-row-value">
                        {viewCar.brand_name || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Number Of Cars</div>
                      <div className="profile-row-value">
                        {viewCar.No_of_carmodel || "—"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="sec-div">
                  <span>SPECIFICATIONS</span>
                </div>
                <div className="col-xl-2 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Passengers</div>
                      <div className="profile-row-value">
                        {viewCar.carmodel_passgrno || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Luggage</div>
                      <div className="profile-row-value">
                        {viewCar.carmodel_luggno || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Doors</div>
                      <div className="profile-row-value">
                        {viewCar.carmodel_doors || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Engine</div>
                      <div className="profile-row-value">
                        {viewCar.engine_capacity_name || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Transmission</div>
                      <div className="profile-row-value">
                        {viewCar.transmission_name || "—"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-2 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Driver Age</div>
                      <div className="profile-row-value">
                        {viewCar.driver_age || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sec-div">
                  <span>FEATURES</span>
                </div>
                <div className="col-xl-2 col-lg-2 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">AC</div>
                      <div className="profile-row-value">
                        {Number(viewCar.carmodel_airconditionr) === 1
                          ? "Yes"
                          : "No"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Airbag</div>
                      <div className="profile-row-value">
                        {Number(viewCar.carmodel_airbag) === 1 ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Central Locking</div>
                      <div className="profile-row-value">
                        {Number(viewCar.carmodel_cntralLocking) === 1
                          ? "Yes"
                          : "No"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Electric Windows</div>
                      <div className="profile-row-value">
                        {Number(viewCar.carmodel_elctricwindows) === 1
                          ? "Yes"
                          : "No"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Stereo</div>
                      <div className="profile-row-value">
                        {Number(viewCar.carmodel_stereo) === 1 ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Display Home</div>
                      <div className="profile-row-value">
                        {Number(viewCar.carmodel_dispinhome) === 1
                          ? "Yes"
                          : "No"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* INCLUDED ITEMS */}

              <div className="sec-div">
                <span>Included Items</span>
              </div>

              <div className="mod-grid">
                {viewCar.added_items?.length > 0 ? (
                  viewCar.added_items.map((item) => (
                    <div key={item.item_id} className="view-mod-chip assigned">
                      <span>✅</span>

                      {item.item_name || "Unnamed item"}
                    </div>
                  ))
                ) : (
                  <p className="no-modules-text">No included items</p>
                )}
              </div>

              {/* DESCRIPTION */}

              {viewCar.carmodel_desc && (
                <div className="mt-4">
                  <div className="sec-div">
                    <span>Description</span>
                  </div>

                  <p className="profile-row-value">{viewCar.carmodel_desc}</p>
                </div>
              )}
            </div>

            {/* FOOTER */}

            <div className="um-modal-foot">
              <button
                className="um-btn-secondary"
                onClick={() => setViewCar(null)}
              >
                Close
              </button>

              <button
                className="um-btn-primary"
                onClick={() => handleEditCar(viewCar)}
              >
                <FiEdit />
                Edit Car
              </button>
            </div>
          </div>
        </div>
      )}

      {/*   DELETE MODAL */}

      {deleteCar && (
        <div
          className="um-overlay"
          onClick={() => !saving && setDeleteCar(null)}
        >
          <div
            className="um-modal um-delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-content">
              {/* DELETE ICON */}

              <div className="delete-icon">
                <FiTrash2 />{" "}
              </div>

              {/* TITLE */}

              <h2 className="delete-title">Delete Car?</h2>

              {/* DESCRIPTION */}

              <p className="delete-description">
                Are you sure you want to delete{" "}
                <strong>{deleteCar.car_name}</strong>
                ?
                <br />
                This action cannot be undone.
              </p>

              {/* ACTIONS */}

              <div className="delete-actions">
                <button
                  type="button"
                  className="um-btn-secondary"
                  disabled={saving}
                  onClick={() => setDeleteCar(null)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="delete-confirm-btn"
                  disabled={saving}
                  onClick={handleDelete}
                >
                  {saving ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      {" "}
                      <FiTrash2 /> Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/*   TOAST */}

      {toast && (
        <div
          className={`um-toast ${toast.type === "error" ? "toast-error" : "toast-success"
            }`}
        >
          {toast.type === "error" ? "⚠️" : "✅"} {toast.msg}
        </div>
      )}
    </Layout>
  );
}
